import { useMemo, useState } from 'react';
import { SUMMARY } from '@/constants/testIds';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';

function fmt(n) {
  return new Intl.NumberFormat('fi-FI').format(n || 0);
}

export default function SummaryPanels({ rows }) {
  const [ryhmaOpen, setRyhmaOpen] = useState(false);
  const [reittiOpen, setReittiOpen] = useState(false);

  const summary = useMemo(() => {
    const ryhma = {};
    const reitti = {};
    let totalKg = 0;
    let activeCount = 0;
    let orderedCount = 0;
    for (const r of rows) {
      const qty = r.order?.maara_kg || 0;
      const route = r.order?.reitti || r.customer.reitti || 'KP';
      const group = r.customer.ryhma || 'Muu';
      totalKg += qty;
      ryhma[group] = (ryhma[group] || 0) + qty;
      reitti[route] = reitti[route] || { kg: 0, count: 0 };
      reitti[route].kg += qty;
      if (qty > 0) {
        reitti[route].count += 1;
        orderedCount += 1;
      }
      activeCount += 1;
    }
    return { totalKg, activeCount, orderedCount, ryhma, reitti };
  }, [rows]);

  const ryhmaEntries = Object.entries(summary.ryhma).sort((a, b) => b[1] - a[1]);
  const reittiEntries = Object.entries(summary.reitti).sort((a, b) => b[1].kg - a[1].kg);

  return (
    <div className="bg-white border border-[#E5E6E3] rounded-xl shadow-sm overflow-hidden">
      {/* KPIs - compact single row */}
      <div className="px-4 py-2.5 flex items-center gap-6 text-sm">
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#6A6D66]">Yhteenveto</span>
        <Kpi label="Yhteensä" value={fmt(summary.totalKg)} unit="kg" testId={SUMMARY.totalKg} accent />
        <Kpi label="Tilauksia" value={fmt(summary.orderedCount)} />
        <Kpi label="Asiakkaita" value={fmt(summary.activeCount)} />
      </div>

      {/* Ryhmä — collapsible */}
      <Collapsible open={ryhmaOpen} onOpenChange={setRyhmaOpen} data-testid={SUMMARY.ryhmaList}>
        <CollapsibleTrigger
          data-testid="summary-ryhma-toggle"
          className="w-full border-t border-[#E5E6E3] px-4 py-1.5 flex items-center justify-between hover:bg-[#F9F9F6] transition-colors text-xs"
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold uppercase tracking-[0.1em] text-[#6A6D66]">Ryhmä</span>
            <span className="text-[#6A6D66]">·</span>
            <span className="text-[#1C1C1A]">{ryhmaEntries.length === 0 ? '—' : ryhmaEntries.map(([k]) => k).join(', ')}</span>
          </div>
          <ChevronDown size={14} className={`text-[#6A6D66] transition-transform ${ryhmaOpen ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-4 pb-3 pt-1">
          {ryhmaEntries.length === 0 && <p className="text-xs text-[#6A6D66]">Ei tilauksia</p>}
          <div className="space-y-1.5">
            {ryhmaEntries.map(([k, v]) => {
              const pct = summary.totalKg ? (v / summary.totalKg) * 100 : 0;
              return (
                <div key={k}>
                  <div className="flex justify-between text-xs">
                    <span className="text-[#1C1C1A]">{k}</span>
                    <span className="font-mono text-[#1C1C1A]">
                      {fmt(v)} kg <span className="text-[#6A6D66] ml-1">{pct.toFixed(1)}%</span>
                    </span>
                  </div>
                  <div className="h-1 rounded-full bg-[#E5E6E3] mt-0.5 overflow-hidden">
                    <div className="h-full bg-[#2A4B30]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Reitti — collapsible */}
      <Collapsible open={reittiOpen} onOpenChange={setReittiOpen} data-testid={SUMMARY.reittiList}>
        <CollapsibleTrigger
          data-testid="summary-reitti-toggle"
          className="w-full border-t border-[#E5E6E3] px-4 py-1.5 flex items-center justify-between hover:bg-[#F9F9F6] transition-colors text-xs"
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold uppercase tracking-[0.1em] text-[#6A6D66]">Reitti</span>
            <span className="text-[#6A6D66]">·</span>
            <span className="text-[#1C1C1A]">{reittiEntries.length === 0 ? '—' : reittiEntries.filter(([_,v]) => v.count>0).map(([k]) => k).join(', ') || reittiEntries.map(([k]) => k).join(', ')}</span>
          </div>
          <ChevronDown size={14} className={`text-[#6A6D66] transition-transform ${reittiOpen ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-4 pb-3 pt-1">
          {reittiEntries.length === 0 && <p className="text-xs text-[#6A6D66]">Ei tilauksia</p>}
          <div className="space-y-1">
            {reittiEntries.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-xs">
                <span className="text-[#1C1C1A]">{k}</span>
                <span className="font-mono text-[#1C1C1A]">
                  {fmt(v.kg)} kg <span className="text-[#6A6D66] ml-1">({v.count})</span>
                </span>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function Kpi({ label, value, unit, testId, accent }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-[#6A6D66]">{label}</span>
      <span data-testid={testId} className={`font-mono font-bold leading-none ${accent ? 'text-base text-[#1C1C1A]' : 'text-sm text-[#1C1C1A]'}`}>
        {value}
        {unit && <span className="text-[10px] font-normal text-[#6A6D66] ml-0.5">{unit}</span>}
      </span>
    </div>
  );
}
